import { motion } from 'framer-motion';
import { Inbox, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Card, EmptyState, SwipeRow, useToast } from '../../components';
import {
  buildCategoryMap,
  groupTransactionsByDate,
  selectAllTransactions,
  useActions,
  useAppData,
} from '../../data';
import type { Transaction } from '../../data/types';
import { formatCurrency } from '../../lib/currency';
import {
  addDays,
  formatLongDate,
  fromIsoDate,
  startOfDay,
  toIsoDate,
} from '../../lib/date';
import { getPayPeriod } from '../../lib/payPeriod';
import { springGentle } from '../../tokens';
import { RecentRow } from '../today/RecentRow';
import {
  ActivityFilters,
  type DateRangeKey,
  type Filters,
} from './ActivityFilters';
import { EditTransactionSheet } from './EditTransactionSheet';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

/** Resolve a date range key to absolute start/end dates. */
function resolveDateRange(
  key: DateRangeKey,
  today: Date,
  profile: ReturnType<typeof useAppData>['profile'],
): { start: Date | null; end: Date | null } {
  const t = startOfDay(today);
  switch (key) {
    case 'period': {
      const p = getPayPeriod(profile, t);
      return { start: p.start, end: p.end };
    }
    case 'month': {
      const start = startOfDay(new Date(t.getFullYear(), t.getMonth(), 1));
      return { start, end: null };
    }
    case '30d':
      return { start: startOfDay(addDays(t, -30)), end: null };
    case '90d':
      return { start: startOfDay(addDays(t, -90)), end: null };
    case 'year':
      return { start: startOfDay(new Date(t.getFullYear(), 0, 1)), end: null };
    case 'all':
      return { start: null, end: null };
  }
}

/** Pretty header label for a date group: "Today" / "Yesterday" / long date. */
function dateHeader(iso: string, today: Date): string {
  const todayIso = toIsoDate(today);
  const yesterdayIso = toIsoDate(addDays(today, -1));
  if (iso === todayIso) return 'Today';
  if (iso === yesterdayIso) return 'Yesterday';
  return formatLongDate(fromIsoDate(iso));
}

export function ActivityScreen() {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const today = useMemo(() => new Date(), []);

  const [filters, setFilters] = useState<Filters>({
    search: '',
    categoryId: 'all',
    dateRange: 'period',
  });
  const [editing, setEditing] = useState<Transaction | null>(null);

  const allTransactions = useMemo(
    () => selectAllTransactions(data),
    [data],
  );
  const categoryMap = useMemo(
    () => buildCategoryMap(data.categories),
    [data.categories],
  );

  // Apply filters.
  const filtered = useMemo(() => {
    const range = resolveDateRange(filters.dateRange, today, data.profile);
    const q = filters.search.trim().toLowerCase();
    return allTransactions.filter((t) => {
      if (filters.categoryId !== 'all' && t.categoryId !== filters.categoryId) {
        return false;
      }
      if (range.start || range.end) {
        const txDate = fromIsoDate(t.date);
        if (range.start && txDate < range.start) return false;
        if (range.end && txDate > range.end) return false;
      }
      if (q) {
        const description = (t.description ?? '').toLowerCase();
        const categoryName = (
          categoryMap.get(t.categoryId)?.name ?? ''
        ).toLowerCase();
        if (!description.includes(q) && !categoryName.includes(q)) return false;
      }
      return true;
    });
  }, [allTransactions, filters, today, data.profile, categoryMap]);

  const groups = useMemo(() => groupTransactionsByDate(filtered), [filtered]);

  const isFilteringActive =
    filters.search.trim().length > 0 ||
    filters.categoryId !== 'all' ||
    filters.dateRange !== 'all';

  const handleDelete = (id: string) => {
    actions.deleteTransaction(id);
    toast.show({ message: 'Transaction deleted', kind: 'info' });
  };

  return (
    <motion.main
      initial="hidden"
      animate="show"
      variants={stagger}
      className="mx-auto max-w-content px-screen-x pt-section-gap"
    >
      <motion.header
        variants={fadeUp}
        transition={springGentle}
        className="mb-card-gap"
      >
        <h1 className="text-section">Activity</h1>
        <p className="mt-1 text-[13px] text-text-secondary">
          {allTransactions.length === 0
            ? 'Your transaction history lives here.'
            : `${allTransactions.length} transaction${
                allTransactions.length === 1 ? '' : 's'
              } total`}
        </p>
      </motion.header>

      <motion.div variants={fadeUp} transition={springGentle}>
        <ActivityFilters
          filters={filters}
          onChange={setFilters}
          categories={data.categories}
          rangeStart={
            resolveDateRange(filters.dateRange, today, data.profile).start
          }
          rangeEnd={
            resolveDateRange(filters.dateRange, today, data.profile).end
          }
        />
      </motion.div>

      {allTransactions.length === 0 ? (
        <motion.div variants={fadeUp} transition={springGentle}>
          <Card flush>
            <EmptyState
              icon={<Inbox size={40} strokeWidth={1.5} />}
              title="No transactions yet"
              description="Add one from the Today tab and it'll show up here, grouped by date with running totals."
            />
          </Card>
        </motion.div>
      ) : groups.length === 0 ? (
        <motion.div variants={fadeUp} transition={springGentle}>
          <Card flush>
            <EmptyState
              icon={<SearchX size={40} strokeWidth={1.5} />}
              title="No matches"
              description={
                isFilteringActive
                  ? 'Try a different search term, category, or date range.'
                  : 'Nothing in this range yet.'
              }
              action={
                isFilteringActive
                  ? {
                      label: 'Clear filters',
                      onClick: () =>
                        setFilters({
                          search: '',
                          categoryId: 'all',
                          dateRange: 'all',
                        }),
                    }
                  : undefined
              }
            />
          </Card>
        </motion.div>
      ) : (
        <motion.div
          variants={fadeUp}
          transition={springGentle}
          className="flex flex-col gap-card-gap"
        >
          {groups.map((g) => (
            <DateGroup
              key={g.date}
              date={g.date}
              total={g.total}
              items={g.items}
              today={today}
              currency={data.profile.currency}
              categoryMap={categoryMap}
              onTapRow={(tx) => setEditing(tx)}
              onDelete={handleDelete}
            />
          ))}
          <SummaryFooter
            transactions={filtered}
            currency={data.profile.currency}
          />
        </motion.div>
      )}

      <EditTransactionSheet
        transaction={editing}
        onClose={() => setEditing(null)}
        onUpdate={(id, draft) => {
          actions.updateTransaction(id, {
            type: draft.type,
            description: draft.description,
            categoryId: draft.categoryId,
            date: draft.date,
            amount: draft.amount,
            spreadDays: draft.spreadDays,
          });
          toast.show({ message: 'Transaction updated', kind: 'success' });
        }}
      />
    </motion.main>
  );
}

function DateGroup({
  date,
  total,
  items,
  today,
  currency,
  categoryMap,
  onTapRow,
  onDelete,
}: {
  date: string;
  total: number;
  items: Transaction[];
  today: Date;
  currency: string;
  categoryMap: ReturnType<typeof buildCategoryMap>;
  onTapRow: (tx: Transaction) => void;
  onDelete: (id: string) => void;
}) {
  // Daily net is muted — the summary doesn't compete with the per-row
  // expense/income colors.
  const formattedTotal = formatCurrency(Math.abs(total), { currency });
  const sign = total > 0 ? '+' : total < 0 ? '-' : '';

  return (
    <div>
      <div className="flex items-baseline justify-between pt-5 pb-2 px-5">
        <span className="text-[14px] font-semibold text-text-primary">
          {dateHeader(date, today)}
        </span>
        <span className="text-[14px] font-medium font-mono tabular-nums text-text-tertiary">
          {sign}
          {formattedTotal}
        </span>
      </div>
      <Card
        flush
        elevation="none"
        className="overflow-hidden border border-surface-secondary/60"
      >
        {items.map((t, i) => (
          <div key={t.id}>
            {i > 0 ? (
              <div className="h-px bg-surface-secondary/60 mx-card-pad" />
            ) : null}
            <SwipeRow onDelete={() => onDelete(t.id)} onTap={() => onTapRow(t)}>
              <RecentRow
                transaction={t}
                category={categoryMap.get(t.categoryId)}
                currency={currency}
                now={today}
                subtitle={categoryMap.get(t.categoryId)?.name}
              />
            </SwipeRow>
          </div>
        ))}
      </Card>
    </div>
  );
}

function SummaryFooter({
  transactions,
  currency,
}: {
  transactions: Transaction[];
  currency: string;
}) {
  if (transactions.length === 0) return null;
  let income = 0;
  let expense = 0;
  let incomeAmt = 0;
  let expenseAmt = 0;
  for (const t of transactions) {
    if (t.type === 'income') {
      income += 1;
      incomeAmt += t.amount;
    } else {
      expense += 1;
      expenseAmt += t.amount;
    }
  }
  const net = incomeAmt - expenseAmt;
  const netSign = net > 0 ? '+' : net < 0 ? '-' : '';
  const netLabel = `${netSign}${formatCurrency(Math.abs(net), { currency })}`;
  return (
    <div className="px-5 py-4 text-[13px] text-text-tertiary text-center tabular-nums">
      Showing {transactions.length} transaction{transactions.length === 1 ? '' : 's'}
      {' · '}
      {income} income {' · '}
      {expense} expense{expense === 1 ? '' : 's'}
      {' · '}
      Net {netLabel}
    </div>
  );
}
